import { Link, useParams } from 'react-router-dom'
import { api } from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { usePlayerParam, usePlayerPaths } from '../../components/playerPaths'

/**
 * The all-time record between two players (FR-040, US6 scenario 2).
 *
 * Across every season, not just this one — the record is the point. Only
 * finished matches count: a scheduled or in-progress one is listed with its
 * state but wins nothing, because a record that counted an unfinished match
 * would change under someone standing at the simulator.
 */
export function HeadToHead() {
  const { pathFor } = usePlayerPaths()
  const { playerId, opponentId } = useParams()
  // Both sides are slugs in the path and public ids to the API. Only the first
  // is canonicalised in the address bar; the opponent rides along in the same
  // URL and is rewritten with it.
  const resolved = usePlayerParam(playerId)
  const { resolve } = usePlayerPaths()
  const resolvedOpponent = resolve(opponentId)
  const h2h = useApi(
    resolved && resolvedOpponent ? (s) => api.headToHead(resolved, resolvedOpponent, s) : null,
    [resolved, resolvedOpponent],
  )

  if (h2h.state === 'loading') return <Loading what="the record" />
  if (h2h.state === 'error') return <Failed what="This record" error={h2h.error} />

  const d = h2h.data
  return (
    <section>
      <h2>
        <Link to={pathFor(d.player_id)}>{d.player_name}</Link> v{' '}
        <Link to={pathFor(d.opponent_id)}>{d.opponent_name}</Link>
      </h2>

      <p className="stat-big" data-testid="h2h-record">
        {d.wins}–{d.losses}
        {d.halved > 0 && `–${d.halved}`}
      </p>
      <p className="stat-note">
        {d.wins} win{d.wins === 1 ? '' : 's'}, {d.losses} loss{d.losses === 1 ? '' : 'es'},{' '}
        {d.halved} halved · all time
      </p>

      <h3>Matches</h3>
      {d.matches.length === 0 ? (
        <Empty>These two have never been paired.</Empty>
      ) : (
        <ul className="match-list">
          {d.matches.map((m) => (
            <li key={m.match_id} className="match-line">
              <Link to={`/events/${m.event.id}`}>
                Week {m.event.week_number} · {m.event.course}
              </Link>
              <span className="match-outcome">
                {m.status === 'won' && m.winner_player_id
                  ? `${m.winner_player_id === d.player_id ? d.player_name : d.opponent_name} ${m.margin ?? ''}`
                  : m.status}
                {m.outcome_note && <em className="mark mark-note"> — {m.outcome_note}</em>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
